import type { Incident } from '../../services/incidentsApi';
import type { SafetyDashboardRow } from '../../services/reportsApi';
import { formatSiteDateTime } from '../siteTime/siteTime';
import { buildRiskPatternReviewPath } from './riskPatternWorkflowReviewContext';

type SafetyDashboardReviewRow = SafetyDashboardRow & {
  grouped_incident_count?: number | null;
};

export const AFTER_SIGNED_SCAN_REVIEW_SCOPE = 'after_signed_scan';
export const FULL_CASE_HISTORY_REVIEW_SCOPE = 'full_case_history';

export type AfterSignedScanTimelineMode =
  | 'since_latest_scan'
  | 'since_scan_custom_end'
  | 'custom_new_activity'
  | 'full_case_history';

export const AFTER_SIGNED_SCAN_TIMELINE_OPTIONS: Array<{
  value: AfterSignedScanTimelineMode;
  label: string;
}> = [
  { value: 'since_latest_scan', label: 'Since latest Signed Safety Scan' },
  { value: 'since_scan_custom_end', label: 'Since latest Signed Safety Scan to custom end' },
  { value: 'custom_new_activity', label: 'Custom new-activity window' },
  { value: 'full_case_history', label: 'Full Safety Case history' },
];

export const START_BEFORE_SIGNED_SCAN_WARNING =
  'This window starts before the latest Signed Safety Scan. It is no longer limited to new activity since sign-off.';

export type ReviewPathTimeBounds = {
  fromDate?: string | null;
  toDate?: string | null;
};

export function isFullCaseHistoryReviewScope(
  searchParams: URLSearchParams | null | undefined,
): boolean {
  return String(searchParams?.get('review_scope') || '').trim() === FULL_CASE_HISTORY_REVIEW_SCOPE;
}

export function appendReviewWindowTimeBounds(
  query: URLSearchParams,
  bounds?: ReviewPathTimeBounds | null,
): void {
  const from = String(bounds?.fromDate || '').trim();
  const to = String(bounds?.toDate || '').trim();
  if (from) query.set('from_date', from);
  else query.delete('from_date');
  if (to) query.set('to_date', to);
  else query.delete('to_date');
}

export function activateFullCaseHistoryReviewParams(
  params: URLSearchParams,
  bounds?: ReviewPathTimeBounds | null,
): void {
  clearAfterSignedScanReviewParams(params);
  params.set('review_scope', FULL_CASE_HISTORY_REVIEW_SCOPE);
  appendReviewWindowTimeBounds(params, bounds);
}

export const AFTER_SIGNED_SCAN_REVIEW_TITLE =
  'Review new activity after latest Signed Safety Scan';

export const AFTER_SIGNED_SCAN_REVIEW_INTRO =
  'This Safety Case already has a Signed Safety Scan. The Signed Safety Scan is frozen and remains unchanged.';

export const AFTER_SIGNED_SCAN_REVIEW_SCOPE_COPY =
  'Only activity detected after the latest Signed Safety Scan.';

export const REVIEW_FULL_SAFETY_CASE_HISTORY_LABEL = 'Review full Safety Case history';

export type AfterSignedScanReviewContext = {
  active: boolean;
  signedScanAt: string | null;
  signedScanScopeStart: string | null;
  signedScanScopeEnd: string | null;
  scanScopeStart: string | null;
  scanScopeEnd: string | null;
  newActivityWindowStart: string | null;
  latestSignedScanId: string | null;
  timelineMode: AfterSignedScanTimelineMode;
  newActivityCount: number | null;
  newActivityLatestObservation: string | null;
  newActivityReviewState: string | null;
  workflowId: string | null;
};

export type AfterSignedScanWindowBounds = {
  startIso: string;
  endIso: string | null;
  timelineMode: AfterSignedScanTimelineMode;
};

export type AfterSignedScanTimelineChange = {
  option: AfterSignedScanTimelineMode;
  scanScopeStart?: string | null;
  scanScopeEnd?: string | null;
};

function normalizeScopeInstant(value?: string | null): string {
  return String(value || '').trim();
}

function scopeInstantsEqual(left?: string | null, right?: string | null): boolean {
  const a = normalizeScopeInstant(left);
  const b = normalizeScopeInstant(right);
  if (!a || !b) return false;
  const aMs = new Date(a).getTime();
  const bMs = new Date(b).getTime();
  if (Number.isFinite(aMs) && Number.isFinite(bMs)) return aMs === bMs;
  return a === b;
}

export function isWindowStartBeforeSignedScan(
  windowStart?: string | null,
  signedScanAt?: string | null,
): boolean {
  const start = normalizeScopeInstant(windowStart);
  const signedAt = normalizeScopeInstant(signedScanAt);
  if (!start || !signedAt) return false;
  const startMs = new Date(start).getTime();
  const signedMs = new Date(signedAt).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(signedMs)) return false;
  return startMs < signedMs;
}

export function deriveAfterSignedScanTimelineMode(
  searchParams: URLSearchParams | null | undefined,
  context: Pick<
    AfterSignedScanReviewContext,
    'active' | 'signedScanAt' | 'scanScopeStart' | 'scanScopeEnd' | 'newActivityWindowStart'
  >,
): AfterSignedScanTimelineMode {
  const params = searchParams || new URLSearchParams();
  const reviewScope = String(params.get('review_scope') || '').trim();
  if (reviewScope === FULL_CASE_HISTORY_REVIEW_SCOPE) return 'full_case_history';
  if (!context.active) return 'since_latest_scan';

  const signedAt = normalizeScopeInstant(context.signedScanAt);
  const start = normalizeScopeInstant(
    context.scanScopeStart || context.newActivityWindowStart || signedAt,
  );
  const hasExplicitEnd = Boolean(normalizeScopeInstant(params.get('scan_scope_end')));
  const hasCustomStart = Boolean(start && signedAt && !scopeInstantsEqual(start, signedAt));

  if (hasCustomStart) return 'custom_new_activity';
  if (hasExplicitEnd) return 'since_scan_custom_end';
  return 'since_latest_scan';
}

export function resolveAfterSignedScanWindowBounds(
  context: Pick<
    AfterSignedScanReviewContext,
    | 'active'
    | 'signedScanAt'
    | 'scanScopeStart'
    | 'scanScopeEnd'
    | 'newActivityWindowStart'
    | 'timelineMode'
  >,
  reviewEnd?: string | null,
): AfterSignedScanWindowBounds | null {
  if (!context.active) return null;
  const signedAt = normalizeScopeInstant(context.signedScanAt);
  if (!signedAt) return null;
  const startIso = normalizeScopeInstant(
    context.scanScopeStart || context.newActivityWindowStart || signedAt,
  ) || signedAt;
  const endIso = normalizeScopeInstant(context.scanScopeEnd || reviewEnd || '') || null;
  return {
    startIso,
    endIso,
    timelineMode: context.timelineMode,
  };
}

export function syncAfterSignedScanScopeUrlParams(
  params: URLSearchParams,
  bounds: {
    signedScanAt: string;
    scanScopeStart: string;
    scanScopeEnd?: string | null;
    latestSignedScanId?: string | null;
    signedScanScopeStart?: string | null;
    signedScanScopeEnd?: string | null;
  },
): void {
  params.set('review_scope', AFTER_SIGNED_SCAN_REVIEW_SCOPE);
  params.set('signed_scan_at', bounds.signedScanAt);
  params.set('scan_scope_start', bounds.scanScopeStart);
  params.set('new_activity_window_start', bounds.scanScopeStart);
  params.set('report_from', bounds.scanScopeStart);
  if (bounds.scanScopeEnd) {
    params.set('scan_scope_end', bounds.scanScopeEnd);
    params.set('report_to', bounds.scanScopeEnd);
  } else {
    params.delete('scan_scope_end');
    params.delete('report_to');
  }
  if (bounds.latestSignedScanId) {
    params.set('latest_signed_scan_id', bounds.latestSignedScanId);
  }
  if (bounds.signedScanScopeStart) {
    params.set('signed_scan_scope_start', bounds.signedScanScopeStart);
  }
  if (bounds.signedScanScopeEnd) {
    params.set('signed_scan_scope_end', bounds.signedScanScopeEnd);
  }
  appendReviewWindowTimeBounds(params, {
    fromDate: bounds.scanScopeStart,
    toDate: bounds.scanScopeEnd || undefined,
  });
}

export function applyAfterSignedScanTimelineChange(
  params: URLSearchParams,
  change: AfterSignedScanTimelineChange,
  anchors: {
    signedScanAt: string;
    reviewEnd?: string | null;
    latestSignedScanId?: string | null;
    signedScanScopeStart?: string | null;
    signedScanScopeEnd?: string | null;
    fullHistoryBounds?: ReviewPathTimeBounds | null;
  },
): void {
  if (change.option === 'full_case_history') {
    activateFullCaseHistoryReviewParams(params, anchors.fullHistoryBounds);
    return;
  }

  const signedAt = normalizeScopeInstant(anchors.signedScanAt);
  if (!signedAt) return;

  if (change.option === 'since_latest_scan') {
    syncAfterSignedScanScopeUrlParams(params, {
      signedScanAt: signedAt,
      scanScopeStart: signedAt,
      scanScopeEnd: null,
      latestSignedScanId: anchors.latestSignedScanId,
      signedScanScopeStart: anchors.signedScanScopeStart,
      signedScanScopeEnd: anchors.signedScanScopeEnd,
    });
    return;
  }

  if (change.option === 'since_scan_custom_end') {
    const endIso = normalizeScopeInstant(change.scanScopeEnd || anchors.reviewEnd);
    syncAfterSignedScanScopeUrlParams(params, {
      signedScanAt: signedAt,
      scanScopeStart: signedAt,
      scanScopeEnd: endIso || null,
      latestSignedScanId: anchors.latestSignedScanId,
      signedScanScopeStart: anchors.signedScanScopeStart,
      signedScanScopeEnd: anchors.signedScanScopeEnd,
    });
    return;
  }

  const startIso = normalizeScopeInstant(change.scanScopeStart || signedAt) || signedAt;
  const endIso = normalizeScopeInstant(change.scanScopeEnd || anchors.reviewEnd) || null;
  syncAfterSignedScanScopeUrlParams(params, {
    signedScanAt: signedAt,
    scanScopeStart: startIso,
    scanScopeEnd: endIso,
    latestSignedScanId: anchors.latestSignedScanId,
    signedScanScopeStart: anchors.signedScanScopeStart,
    signedScanScopeEnd: anchors.signedScanScopeEnd,
  });
}

export type AfterSignedScanWorkflowHydrationSource = {
  hasSignedSafetyScan?: boolean | null;
  latestSignedScanAt?: string | null;
  signedScanScopeStart?: string | null;
  signedScanScopeEnd?: string | null;
  latestSignedScanReportId?: string | null;
  latestObservationAt?: string | null;
};

export function resolveEffectiveAfterSignedScanReviewContext(
  searchParams: URLSearchParams | null | undefined,
  workflowContext?: AfterSignedScanWorkflowHydrationSource | null,
): AfterSignedScanReviewContext {
  const parsed = parseAfterSignedScanReviewContext(searchParams);
  if (parsed.active) return parsed;

  const signedScanAt = String(workflowContext?.latestSignedScanAt || '').trim();
  if (!workflowContext?.hasSignedSafetyScan || !signedScanAt) return parsed;

  const scanScopeStart = String(parsed.scanScopeStart || signedScanAt).trim() || signedScanAt;
  const hydrated = {
    ...parsed,
    active: true,
    signedScanAt,
    signedScanScopeStart: parsed.signedScanScopeStart || workflowContext.signedScanScopeStart || null,
    signedScanScopeEnd: parsed.signedScanScopeEnd || workflowContext.signedScanScopeEnd || null,
    scanScopeStart,
    scanScopeEnd: parsed.scanScopeEnd,
    newActivityWindowStart: parsed.newActivityWindowStart || signedScanAt,
    latestSignedScanId: parsed.latestSignedScanId || workflowContext.latestSignedScanReportId || null,
    newActivityLatestObservation: parsed.newActivityLatestObservation || workflowContext.latestObservationAt || null,
  };
  return {
    ...hydrated,
    timelineMode: deriveAfterSignedScanTimelineMode(searchParams, hydrated),
  };
}

export function parseAfterSignedScanReviewContext(
  searchParams: URLSearchParams | null | undefined,
): AfterSignedScanReviewContext {
  const params = searchParams || new URLSearchParams();
  const active = String(params.get('review_scope') || '').trim() === AFTER_SIGNED_SCAN_REVIEW_SCOPE;
  const countRaw = String(params.get('new_activity_count') || '').trim();
  const parsedCount = countRaw ? Number(countRaw) : NaN;
  const signedScanAt = String(params.get('signed_scan_at') || '').trim() || null;
  const scanScopeStart = String(params.get('scan_scope_start') || '').trim() || null;
  const scanScopeEnd = String(params.get('scan_scope_end') || '').trim() || null;
  const newActivityWindowStart = String(params.get('new_activity_window_start') || '').trim() || null;
  const partial = {
    active,
    signedScanAt,
    signedScanScopeStart: String(params.get('signed_scan_scope_start') || '').trim() || null,
    signedScanScopeEnd: String(params.get('signed_scan_scope_end') || '').trim() || null,
    scanScopeStart,
    scanScopeEnd,
    newActivityWindowStart,
    latestSignedScanId: String(params.get('latest_signed_scan_id') || '').trim() || null,
    newActivityCount: Number.isFinite(parsedCount) ? parsedCount : null,
    newActivityLatestObservation: String(params.get('new_activity_latest_observation') || '').trim() || null,
    newActivityReviewState: String(params.get('new_activity_review_state') || '').trim() || null,
    workflowId: String(params.get('workflow_id') || '').trim() || null,
  };
  return {
    ...partial,
    timelineMode: deriveAfterSignedScanTimelineMode(params, partial),
  };
}

export function clearAfterSignedScanReviewParams(params: URLSearchParams): void {
  params.delete('review_scope');
  params.delete('signed_scan_at');
  params.delete('signed_scan_scope_start');
  params.delete('signed_scan_scope_end');
  params.delete('scan_scope_start');
  params.delete('scan_scope_end');
  params.delete('new_activity_window_start');
  params.delete('latest_signed_scan_id');
  params.delete('new_activity_count');
  params.delete('new_activity_latest_observation');
  params.delete('new_activity_review_state');
}

export function incidentObservationTimestamp(incident: Incident): number {
  const raw = incident.last_edge_update_at
    || incident.updated_at
    || incident.created_at
    || (incident as { ts_start?: string | null }).ts_start;
  const ms = new Date(String(raw || '')).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

export type AfterSignedScanIncidentAnchor = {
  incidentType?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  deviceId?: string | null;
};

export function filterIncidentsAfterSignedScan(
  incidents: Incident[],
  signedScanAtIso: string,
  anchor?: AfterSignedScanIncidentAnchor,
): Incident[] {
  return filterIncidentsInAfterSignedScanWindow(
    incidents,
    signedScanAtIso,
    null,
    anchor,
  );
}

export function filterIncidentsInAfterSignedScanWindow(
  incidents: Incident[],
  windowStartIso: string,
  windowEndIso?: string | null,
  anchor?: AfterSignedScanIncidentAnchor,
): Incident[] {
  const startMs = new Date(String(windowStartIso || '')).getTime();
  if (!Number.isFinite(startMs)) return [];
  const endRaw = String(windowEndIso || '').trim();
  const endMs = endRaw ? new Date(endRaw).getTime() : Number.POSITIVE_INFINITY;
  return incidents.filter((incident) => {
    if (anchor?.incidentType && String(incident.incident_type || '').trim() !== String(anchor.incidentType).trim()) {
      return false;
    }
    if (anchor?.zoneId && String(incident.zone_id || '').trim() !== String(anchor.zoneId).trim()) {
      return false;
    }
    if (anchor?.cameraId && String(incident.camera_id || '').trim() !== String(anchor.cameraId).trim()) {
      return false;
    }
    if (anchor?.deviceId && String(incident.device_id || '').trim() !== String(anchor.deviceId).trim()) {
      return false;
    }
    const observationMs = incidentObservationTimestamp(incident);
    if (observationMs <= startMs) return false;
    if (Number.isFinite(endMs) && observationMs > endMs) return false;
    return true;
  });
}

export function formatSignedScanScopeStartLabel(start?: string | null, timeZone?: string | null): string {
  return formatAfterSignedScanTimestamp(start, timeZone);
}

export function formatSignedScanScopeEndLabel(end?: string | null, timeZone?: string | null): string {
  return formatAfterSignedScanTimestamp(end, timeZone);
}

export function formatSignedScanScopeLabel(
  start?: string | null,
  end?: string | null,
  timeZone?: string | null,
): string {
  const startLabel = formatSignedScanScopeStartLabel(start, timeZone);
  const endLabel = formatSignedScanScopeEndLabel(end, timeZone);
  const startRecorded = startLabel !== 'Not recorded';
  const endRecorded = endLabel !== 'Not recorded';
  if (startRecorded && endRecorded) return `${startLabel} → ${endLabel}`;
  if (endRecorded) return `Through ${endLabel}`;
  if (startRecorded) return `From ${startLabel}`;
  return 'Not recorded';
}

export function formatAfterSignedScanTimestamp(value?: string | null, timeZone?: string | null): string {
  const text = String(value || '').trim();
  if (!text) return 'Not recorded';
  return formatSiteDateTime(text, timeZone) || text;
}

export function buildAfterSignedScanReviewPath(
  row: SafetyDashboardReviewRow,
  fallbackSiteId: string,
  timeBounds?: ReviewPathTimeBounds | null,
): string {
  const query = new URLSearchParams(buildRiskPatternReviewPath(row, fallbackSiteId).split('?')[1] || '');
  query.set('review_scope', AFTER_SIGNED_SCAN_REVIEW_SCOPE);
  if (row.report_finalized_at) query.set('signed_scan_at', String(row.report_finalized_at));
  if (row.report_scope_start) query.set('signed_scan_scope_start', String(row.report_scope_start));
  if (row.report_scope_end) query.set('signed_scan_scope_end', String(row.report_scope_end));
  const count = row.grouped_incident_count ?? row.impact_summary?.impact_measurement?.followup_incident_count;
  if (count != null && Number.isFinite(Number(count))) query.set('new_activity_count', String(Number(count)));
  const reviewState = String(row.latest_review_status_label || row.latest_review_status || '').trim();
  if (reviewState) query.set('new_activity_review_state', reviewState);
  const latestObservation = row.source_workflow_updated_at || row.updated_at;
  if (latestObservation) query.set('new_activity_latest_observation', String(latestObservation));
  appendReviewWindowTimeBounds(query, timeBounds);
  return `/incidents?${query.toString()}`;
}
